import { useEffect, useRef } from 'react';

/**
 * Shared canvas animation lifecycle for screensaver themes.
 *
 * init(ctx, w, h, state) runs on mount + resize — seed particles here.
 * draw(ctx, w, h, state, t) runs every frame.
 * state is a mutable object persisted across frames (not React state).
 */
export default function useThemeCanvas({ init, draw }) {
  const canvasRef = useRef(null);
  const stateRef = useRef({});
  const rafRef = useRef(null);
  const initRef = useRef(init);
  const drawRef = useRef(draw);

  // Keep latest callbacks without retriggering the effect each render
  initRef.current = init;
  drawRef.current = draw;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const state = stateRef.current;
    let running = true;
    let startTime = performance.now();
    let lastT = 0;

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
      canvas.style.width = window.innerWidth + 'px';
      canvas.style.height = window.innerHeight + 'px';
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);
      initRef.current && initRef.current(ctx, window.innerWidth, window.innerHeight, state);
    };
    resize();
    window.addEventListener('resize', resize);

    const loop = (now) => {
      if (!running) return;
      const t = (now - startTime) / 1000;
      lastT = t;
      drawRef.current(ctx, window.innerWidth, window.innerHeight, state, t);
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);

    const onVisibility = () => {
      if (document.hidden) {
        running = false;
        cancelAnimationFrame(rafRef.current);
      } else {
        running = true;
        startTime = performance.now() - lastT * 1000; // keep t continuous across pauses
        rafRef.current = requestAnimationFrame(loop);
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      running = false;
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener('resize', resize);
      document.removeEventListener('visibilitychange', onVisibility);
      stateRef.current = {};
    };
  }, []);

  return canvasRef;
}

// Hex -> {r,g,b}. Falls back to a safe blue if given a non-string or malformed value.
const FALLBACK_RGB = { r: 59, g: 130, b: 246 };

export function hexToRgb(hex) {
  if (typeof hex !== 'string' || hex.length === 0) return FALLBACK_RGB;
  const h = hex.replace('#', '');
  if (!/^[0-9a-fA-F]+$/.test(h) || (h.length !== 3 && h.length !== 6)) return FALLBACK_RGB;
  const n = parseInt(h.length === 3 ? h.split('').map(c => c + c).join('') : h, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

export function rgba(hex, alpha) {
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r},${g},${b},${alpha})`;
}

// Cycle through palette based on time (for "cycling" color mode)
export function paletteColor(palette, t, index = 0) {
  if (!palette || palette.length === 0) return '#3b82f6';
  const i = (Math.floor(t * 0.15) + index) % palette.length;
  return palette[i];
}

// Smoothly interpolated palette color — returns rgb() string
export function palettePulse(palette, t, speed = 0.15) {
  const { r, g, b } = pulseRgb(palette, t, speed);
  return `rgb(${r},${g},${b})`;
}

// Smoothly interpolated palette color — returns {r,g,b} components
export function pulseRgb(palette, t, speed = 0.15) {
  if (!palette || palette.length === 0) return { r: 59, g: 130, b: 246 };
  if (palette.length === 1) return hexToRgb(palette[0]);
  const scaled = t * speed;
  const i = Math.floor(scaled) % palette.length;
  const j = (i + 1) % palette.length;
  const k = scaled - Math.floor(scaled);
  const a = hexToRgb(palette[i]);
  const b = hexToRgb(palette[j]);
  return {
    r: Math.round(a.r + (b.r - a.r) * k),
    g: Math.round(a.g + (b.g - a.g) * k),
    b: Math.round(a.b + (b.b - a.b) * k),
  };
}
